import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useConfirm } from './ConfirmModal';
import API from '../utils/api';
import { isNative } from '../utils/capacitor';
import { trackVoiceCloneCreated, trackVoiceCloneStarted } from '../utils/analytics';

const RECORD_SECONDS = 30;

// Prompted lines to read during recording — keeps the sample varied and natural
const PROMPTS = [
  '"Once upon a time, in a land far away, there lived a kind and curious child…"',
  '"The moon was bright that night as our little hero set off on an adventure…"',
  '"Deep in the enchanted forest, where the trees whispered secrets to the wind…"',
  '"And so, with a heart full of bravery and a smile full of wonder, the journey began…"',
];

const STATES = {
  IDLE:       'idle',        // no voice profile, nothing happening
  READY:      'ready',       // has a voice profile
  REQUESTING: 'requesting',  // asking for mic permission
  RECORDING:  'recording',   // actively recording
  UPLOADING:  'uploading',   // sending to backend for processing
  SUCCESS:    'success',     // just saved successfully
  ERROR:      'error',       // something went wrong
};

/**
 * VoiceRecorder
 * Props:
 *   onProfileChange(profile|null) — called when the profile is created or deleted
 */
const VoiceRecorder = ({ onProfileChange }) => {
  const confirm = useConfirm();
  const [uiState, setUiState]         = useState(STATES.IDLE);
  const [profile, setProfile]         = useState(null);   // existing voice profile from server
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(RECORD_SECONDS);
  const [errorMsg, setErrorMsg]       = useState('');
  const [promptIdx, setPromptIdx]     = useState(0);
  const [audioBlob, setAudioBlob]     = useState(null);   // recorded blob (for playback preview)
  const [previewing, setPreviewing]   = useState(false);
  const [deleting, setDeleting]       = useState(false);

  // Waveform canvas
  const canvasRef    = useRef(null);
  const animFrameRef = useRef(null);

  // MediaRecorder
  const streamRef    = useRef(null);
  const recorderRef  = useRef(null);
  const chunksRef    = useRef([]);
  const timerRef     = useRef(null);
  const startTimeRef = useRef(null);

  // Preview audio
  const previewAudioRef = useRef(null);

  // ── load existing profile ──────────────────────────────────────────────
  useEffect(() => {
    API.get('/voice/profile')
      .then(res => {
        if (res.data.profile) {
          setProfile(res.data.profile);
          setUiState(STATES.READY);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  // ── cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      cancelAnimationFrame(animFrameRef.current);
      stopStream();
    };
  }, []);

  // ── waveform visualiser ────────────────────────────────────────────────
  const startVisualiser = useCallback((stream) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const audioCtx  = new AudioContext();
    const source    = audioCtx.createMediaStreamSource(stream);
    const analyser  = audioCtx.createAnalyser();
    analyser.fftSize = 128;
    source.connect(analyser);
    const bufLen = analyser.frequencyBinCount;
    const data   = new Uint8Array(bufLen);

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(data);
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      const barW = (W / bufLen) * 2;
      let x = 0;
      for (let i = 0; i < bufLen; i++) {
        const barH = (data[i] / 255) * H;
        const alpha = 0.4 + (data[i] / 255) * 0.6;
        ctx.fillStyle = `rgba(111, 66, 193, ${alpha})`;
        ctx.fillRect(x, H - barH, barW - 2, barH);
        x += barW;
      }
    };
    draw();

    // return cleanup
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      audioCtx.close();
    };
  }, []);

  // ── start recording ────────────────────────────────────────────────────
  const handleStartRecording = async () => {
    setErrorMsg('');
    setAudioBlob(null);
    setUiState(STATES.REQUESTING);

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch (err) {
      // Tell the two failures apart — the fix differs, and inside the native app
      // the microphone toggle lives in the OS settings, not in a browser.
      const denied = err?.name === 'NotAllowedError' || err?.name === 'SecurityError';
      const noMic  = err?.name === 'NotFoundError'   || err?.name === 'OverconstrainedError';
      setErrorMsg(
        noMic
          ? 'No microphone was found. Connect one — or check no other app is using it — and try again.'
          : denied && isNative()
            ? 'Microphone access was denied. Allow the Microphone permission for Dreamy Bedtime in your device Settings, then try again.'
            : denied
              ? 'Microphone access was denied. Please allow mic access in your browser settings and try again.'
              : 'We couldn’t start recording. Please try again.'
      );
      setUiState(profile ? STATES.READY : STATES.IDLE);
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    // Pick the best supported MIME type.
    // Prefer mp4 (Safari/iOS) and ogg over webm — Cartesia IVC handles them more reliably.
    const mimeType = MediaRecorder.isTypeSupported('audio/mp4')
      ? 'audio/mp4'
      : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
        ? 'audio/ogg;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
            ? 'audio/webm'
            : '';

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const mime = mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mime });
      // Store the actual mime on the blob so the upload step can use it
      blob._recordedMime = mime;
      setAudioBlob(blob);
      stopStream();
      cancelAnimationFrame(animFrameRef.current);
      // Clear canvas
      const canvas = canvasRef.current;
      if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    };

    recorder.start(100); // collect data every 100ms
    startTimeRef.current = Date.now();
    setSecondsLeft(RECORD_SECONDS);
    trackVoiceCloneStarted();
    setUiState(STATES.RECORDING);
    setPromptIdx(Math.floor(Math.random() * PROMPTS.length));

    // Start waveform
    const cleanupVisualiser = startVisualiser(stream);

    // Countdown timer
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const left    = RECORD_SECONDS - elapsed;
      setSecondsLeft(Math.max(0, left));
      if (left <= 0) {
        clearInterval(timerRef.current);
        cleanupVisualiser?.();
        recorder.stop();
        setUiState(STATES.UPLOADING);
      }
    }, 250);
  };

  const handleStopEarly = () => {
    clearInterval(timerRef.current);
    cancelAnimationFrame(animFrameRef.current);
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    setUiState(STATES.UPLOADING);
  };

  // ── upload when blob is ready and state is UPLOADING ──────────────────
  useEffect(() => {
    if (uiState !== STATES.UPLOADING || !audioBlob) return;

    const upload = async () => {
      try {
        const elapsed = Math.floor((Date.now() - (startTimeRef.current || Date.now())) / 1000);
        const duration = Math.min(elapsed, RECORD_SECONDS);

        if (duration < 5) {
          setErrorMsg('Recording is too short. Please record at least 5 seconds for a usable voice sample.');
          setUiState(profile ? STATES.READY : STATES.IDLE);
          return;
        }

        const mime = audioBlob.type || audioBlob._recordedMime || 'audio/webm';
        const ext  = mime.includes('mp4') ? 'mp4'
                   : mime.includes('ogg') ? 'ogg'
                   : mime.includes('wav') ? 'wav'
                   : 'webm';

        const formData = new FormData();
        formData.append('audio', audioBlob, `voice_sample.${ext}`);
        formData.append('duration', String(duration));

        // Do NOT set Content-Type manually — axios auto-sets multipart/form-data
        // with the correct boundary when the body is a FormData object.
        // Must outlast the server's own retry budget (3 attempts x 45s + backoff
        // ~= 141s). At 60s the browser gave up while the server was still
        // working, so a clone that then succeeded looked like a failure.
        const res = await API.post('/voice/upload', formData, {
          timeout: 150000,
        });

        const newProfile = res.data.profile;
        setProfile(newProfile);
        setUiState(STATES.SUCCESS);
        trackVoiceCloneCreated();
        onProfileChange?.(newProfile);

        // Transition to READY after 2.5s
        setTimeout(() => setUiState(STATES.READY), 2500);
      } catch (err) {
        const msg = err.response?.data?.message || 'Upload failed. Please try again.';
        setErrorMsg(msg);
        setUiState(profile ? STATES.READY : STATES.IDLE);
      }
    };

    upload();
  }, [uiState, audioBlob]); // eslint-disable-line

  // ── preview playback ───────────────────────────────────────────────────
  const handleTogglePreview = () => {
    if (!audioBlob) return;
    if (!previewAudioRef.current) {
      previewAudioRef.current = new Audio(URL.createObjectURL(audioBlob));
      previewAudioRef.current.onended = () => setPreviewing(false);
    }
    if (previewing) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
      setPreviewing(false);
    } else {
      previewAudioRef.current.play();
      setPreviewing(true);
    }
  };

  // ── delete voice profile ───────────────────────────────────────────────
  const handleDelete = async () => {
    const ok = await confirm({ title: 'Delete voice clone?', message: 'Your voice clone will be permanently deleted. Stories will fall back to the standard narration voice.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    setDeleting(true);
    try {
      await API.delete('/voice/profile');
      setProfile(null);
      setAudioBlob(null);
      setUiState(STATES.IDLE);
      onProfileChange?.(null);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to delete voice profile.');
    } finally {
      setDeleting(false);
    }
  };

  // ── reset to record again ──────────────────────────────────────────────
  const handleReRecord = () => {
    setAudioBlob(null);
    setPreviewing(false);
    setErrorMsg('');
    setUiState(STATES.IDLE);
  };

  // ── render ─────────────────────────────────────────────────────────────
  if (loadingProfile) {
    return (
      <div className="text-center py-4">
        <span className="spinner-border spinner-border-sm" style={{ color: '#6f42c1' }}></span>
      </div>
    );
  }

  const progressPct = Math.round(((RECORD_SECONDS - secondsLeft) / RECORD_SECONDS) * 100);

  return (
    <div>
      {errorMsg && (
        <div className="alert alert-danger py-2 small rounded-3 d-flex align-items-center gap-2 mb-3">
          <i className="bi bi-exclamation-circle-fill flex-shrink-0"></i>
          <span>{errorMsg}</span>
          <button className="btn-close ms-auto btn-close-sm" onClick={() => setErrorMsg('')}></button>
        </div>
      )}

      {/* ── READY state — profile exists ── */}
      {(uiState === STATES.READY || uiState === STATES.SUCCESS) && profile && (
        <div className="p-4 rounded-4" style={{ background: 'var(--green-section)', border: '2px solid #86efac' }}>
          {uiState === STATES.SUCCESS && (
            <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded-3"
              style={{ background: '#dcfce7', border: '1px solid #86efac' }}>
              <i className="bi bi-check-circle-fill" style={{ color: '#16a34a', fontSize: '18px' }}></i>
              <span className="fw-semibold small" style={{ color: '#15803d' }}>
                Voice clone created successfully! Stories will now be narrated in your voice.
              </span>
            </div>
          )}
          <div className="d-flex align-items-center gap-3 mb-3">
            <div className="rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: '52px', height: '52px', background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)', fontSize: '22px', flexShrink: 0 }}>
              🎙️
            </div>
            <div>
              <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{profile.name}</div>
              <div className="text-muted small">
                {profile.durationSecs && `${profile.durationSecs}s recording · `}
                Created {new Date(profile.createdAt).toLocaleDateString()}
              </div>
              <span className="badge rounded-pill mt-1" style={{ background: '#16a34a', color: 'white', fontSize: '10px' }}>
                ✓ Ready to use
              </span>
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
              onClick={handleReRecord}>
              <i className="bi bi-arrow-clockwise me-1"></i>Re-record
            </button>
            <button className="btn btn-sm btn-outline-danger rounded-pill px-3"
              onClick={handleDelete} disabled={deleting}>
              {deleting
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Deleting…</>
                : <><i className="bi bi-trash me-1"></i>Delete Voice</>}
            </button>
          </div>
        </div>
      )}

      {/* ── IDLE state — no profile yet ── */}
      {uiState === STATES.IDLE && (
        <div className="text-center py-3">
          <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
            style={{ width: '72px', height: '72px', background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)', fontSize: '32px' }}>
            🎙️
          </div>
          <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Record your voice</h6>
          <p className="text-muted small mb-4" style={{ maxWidth: '360px', margin: '0 auto 20px' }}>
            Read aloud for 30 seconds. Dreamy Bedtime will clone your voice using smart technology and use it for every story narration.
            <br />
            <strong style={{ color: '#6f42c1' }}>Your children will hear bedtime stories in your own voice.</strong>
          </p>
          <button className="btn btn-dream rounded-pill px-5 fw-semibold"
            onClick={handleStartRecording}>
            <i className="bi bi-mic-fill me-2"></i>Start Recording
          </button>
          <p className="text-muted mt-2 mb-0" style={{ fontSize: '12px' }}>
            <i className="bi bi-lock-fill me-1"></i>Your recording is used only to create your voice clone and is not stored on our servers.
          </p>
        </div>
      )}

      {/* ── REQUESTING state ── */}
      {uiState === STATES.REQUESTING && (
        <div className="text-center py-4">
          <div className="spinner-border mb-3" style={{ color: '#6f42c1' }}></div>
          <p className="text-muted small">Requesting microphone access…</p>
        </div>
      )}

      {/* ── RECORDING state ── */}
      {uiState === STATES.RECORDING && (
        <div className="p-4 rounded-4" style={{ background: 'linear-gradient(135deg,#fdf4ff,#f0e8ff)', border: '2px solid #c9a8ff' }}>
          {/* Timer ring */}
          <div className="text-center mb-3">
            <div className="position-relative d-inline-block">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="44" fill="none" stroke="#e8d5ff" strokeWidth="8" />
                <circle cx="50" cy="50" r="44" fill="none" stroke="#6f42c1" strokeWidth="8"
                  strokeDasharray={`${2 * Math.PI * 44}`}
                  strokeDashoffset={`${2 * Math.PI * 44 * (1 - progressPct / 100)}`}
                  strokeLinecap="round"
                  transform="rotate(-90 50 50)"
                  style={{ transition: 'stroke-dashoffset 0.25s linear' }} />
              </svg>
              <div className="position-absolute top-50 start-50 translate-middle text-center">
                <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '22px', lineHeight: 1 }}>{secondsLeft}</div>
                <div style={{ color: '#6f42c1', fontSize: '11px' }}>secs</div>
              </div>
            </div>
          </div>

          {/* Waveform */}
          <canvas ref={canvasRef} width={400} height={60}
            className="w-100 rounded-3 mb-3"
            style={{ display: 'block', background: 'rgba(111,66,193,0.06)' }} />

          {/* Prompt card */}
          {/* Themed surface, not hardcoded white: the prompt below is coloured
              with --text-main (pale lavender in dark mode), so on a white card
              the line the user has to read aloud was invisible. */}
          <div className="p-3 rounded-3 mb-3" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-purple)' }}>
            <div className="text-muted small mb-1">
              <i className="bi bi-chat-quote me-1" style={{ color: '#6f42c1' }}></i>Read this aloud:
            </div>
            <p className="mb-0 fst-italic fw-semibold" style={{ color: 'var(--text-main)', fontSize: '14px', lineHeight: '1.6' }}>
              {PROMPTS[promptIdx]}
            </p>
          </div>

          {/* Recording indicator */}
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <span className="rounded-circle" style={{ width: '10px', height: '10px', background: '#ef4444', display: 'inline-block', animation: 'pulse 1s infinite' }}></span>
              <span className="fw-semibold small" style={{ color: '#ef4444' }}>Recording…</span>
            </div>
            <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" onClick={handleStopEarly}>
              <i className="bi bi-stop-fill me-1"></i>Done ({secondsLeft}s remaining)
            </button>
          </div>
        </div>
      )}

      {/* ── UPLOADING state ── */}
      {uiState === STATES.UPLOADING && (
        <div className="p-4 rounded-4 text-center" style={{ background: 'var(--page-bg)', border: '1px dashed #c9a8ff' }}>
          {/* Preview */}
          {audioBlob && (
            <div className="mb-4">
              <button className="btn btn-sm btn-outline-secondary rounded-pill px-3 me-2" onClick={handleTogglePreview}>
                <i className={`bi ${previewing ? 'bi-pause-fill' : 'bi-play-fill'} me-1`}></i>
                {previewing ? 'Pause preview' : 'Preview recording'}
              </button>
            </div>
          )}
          <div className="spinner-border mb-3" style={{ color: '#6f42c1', width: '2.5rem', height: '2.5rem' }}></div>
          <div className="fw-semibold mb-1" style={{ color: 'var(--text-main)' }}>Creating your voice clone…</div>
          <p className="text-muted small mb-0">
            Dreamy Bedtime is analysing your voice. This usually takes 10–20 seconds.
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
